import { Component, HostListener, Input, OnChanges, ViewChild } from '@angular/core';
import { LegHistoryTimelineComponent } from './detail-modals/leg-history-timeline/leg-history-timeline.component';
import { Detail, Properties, TripHistory } from '../interfaces/trip-history';
import { LegHistoryComponent } from './detail-modals/leg-history/leg-history.component';
import { LegUpdateHistoryComponent } from './detail-modals/leg-update-history/leg-update-history.component';
import { TripAssignedHistoryComponent } from './detail-modals/trip-assigned-history/trip-assigned-history.component';
import { DialogService } from 'src/app/core/services/dialog.service';
import { ApiHttpService } from 'src/app/core/services/apiHttpcall.service';
import { Response } from '../interfaces/response';
import { NgxSpinnerService } from 'ngx-spinner';
import { LegHistoryData } from './detail-modals/leg-history/leg-history.interface';
import { STATUS_MAP } from './trip-status.enum';

@Component({
  selector: 'trip-history-sidebar',
  templateUrl: './trip-history-sidebar.component.html',
  styleUrls: ['./trip-history-sidebar.component.scss']
})
export class TripHistorySidebarComponent implements OnChanges {
  @ViewChild('tripLegHistoryTimeline_Ref') tripLegHistoryTimeline_Ref: LegHistoryTimelineComponent;
  @ViewChild('tripLegHistory_Ref') tripLegHistory_Ref: LegHistoryComponent;
  @ViewChild('tripUpdateHistory_ref') tripUpdateHistory_ref: LegUpdateHistoryComponent;
  @ViewChild('tripAssignHistoryRef') tripAssignHistoryRef: TripAssignedHistoryComponent;
  @Input() moduleName: string = '';
  @Input() historyData: TripHistory[] = [];
  @Input() tripId: number;


  @HostListener('document:keydown.escape', ['$event'])
  onEscPressed(event: KeyboardEvent) {
    if (this.dialogService.isTopMost(this)) {
      this.hide();
    }
  }

  visibility: boolean = false;
  events: TripHistory[];

  notClickableLogs = [
    'Trip Created',
    'Trip Accepted',
    'Trip Started',
    'Trip Completed'
  ];

  constructor(
    private readonly dialogService: DialogService,
    private readonly httpService: ApiHttpService,
    private readonly ngxSpinnerService: NgxSpinnerService,
  ) { }

  ngOnChanges() {
    this.events = this.historyData;
  }

  show() {
    this.visibility = true;
    this.dialogService.registerDialog(this);
    
    // Make API call for trip-requests, leads, customers, rejectedlead, inactivelead, and inactive-customers modules
    if (this.moduleName === 'trip-requests' || this.moduleName === 'leads' || this.moduleName === 'customers' || this.moduleName === 'rejectedlead' || this.moduleName === 'inactivelead' || this.moduleName === 'inactive-customers') {
      this.getTripHistoryLog(this.tripId);
    }
  }

  onLogTitleClick(event: TripHistory) {
    const header = this.getProperties(event.properties).header;
    const detail = this.getProperties(event.properties).detail;
    if (!this.isClickable(event.description)) return;
    switch (event.description) {
      case 'Leg Reverted':
      case 'Trip Assignment Updated':
        this.tripLegHistoryTimeline_Ref.title = event.description;
        this.tripLegHistoryTimeline_Ref.date = event.created_at;
        this.tripLegHistoryTimeline_Ref.data = detail as Detail;
        this.tripLegHistoryTimeline_Ref.legName = header['Leg Name'];
        this.tripLegHistoryTimeline_Ref.logType = event.log_type;
        this.tripLegHistoryTimeline_Ref.show();
        break;
      case 'Trip Updated':
        this.tripUpdateHistory_ref.title = event.description;
        this.tripUpdateHistory_ref.legDate = event.created_at;
        this.tripUpdateHistory_ref.data = detail;
        this.tripUpdateHistory_ref.show();
        break;
      case 'Trip Assigned':
        this.tripAssignHistoryRef.setTitle('Trip Details');
        this.tripAssignHistoryRef.show();
        break;
      default:
        this.tripLegHistory_Ref.title = event.description;
        this.tripLegHistory_Ref.data = (detail as any)[0] as LegHistoryData;
        // this.tripLegHistory_Ref.getDetails((detail as string[])[0])
        this.tripLegHistory_Ref.date = event.created_at;
        this.tripLegHistory_Ref.show();
        return;
    }
  }

  hide() {
    this.visibility = false;
    this.dialogService.unregisterDialog(this);
  }

  getChangeValues(change: any) {
    return change.value as { new: string, old: string };
  }

  hasUpdatedEvent(): boolean {
    return this.events.some((item: any) => item.event === 'updated');
  }

  getProperties(property: string[]) {
    const properties = JSON.parse(property[0]) as Properties<Detail>;
    return properties;
  }

  disableSorting() {
    return 0;
  }

  isClickable(logName: string): boolean {
    // For leads module, make all actions non-clickable
    if (this.moduleName === 'leads' || this.moduleName == 'customers' || this.moduleName == 'rejectedlead' || this.moduleName === 'inactive-customers') {
      return false;
    }
    return !this.notClickableLogs.includes(logName);
  }

  async getTripHistoryLog(id: number) {
    this.ngxSpinnerService.show();
    let url = '';
    
    try {
      // Use different API endpoints based on module
      if (this.moduleName === 'leads' || this.moduleName === 'customers' || this.moduleName === 'rejectedlead' || this.moduleName === 'inactive-customers') {
        url = `/leads/${id}/history`;
      } else {
        url = `/get-logs/${id}`;
      }
      
      const resp = await this.httpService.get<Response<TripHistory[]>>(url);
      if(resp) this.ngxSpinnerService.hide();
      
      if (resp.status == 200) {
        if (this.moduleName === 'leads' || this.moduleName === 'customers' || this.moduleName === 'rejectedlead' || this.moduleName === 'inactive-customers') {
          // Transform leads/customers/rejectedlead/inactive-customers history data to match TripHistory format
          this.historyData = this.transformLeadsHistoryData(resp.data);
        } else {
          this.historyData = resp.data;
        }
        
        // If no history data after transformation, set empty array
        if (!this.historyData || this.historyData.length === 0) {
          this.historyData = [];
        }
      } else {
        // Handle API error - set empty array to show "No History Found" message
        this.historyData = [];
      }
    } catch (error) {
      // Handle network or other errors
      this.ngxSpinnerService.hide();
      this.historyData = [];
      console.error('Error fetching history data:', error);
    }
  }

  // Transform leads history data to match TripHistory format
  private transformLeadsHistoryData(apiResponse: any): TripHistory[] {
    let apiData = [];
    apiData = apiResponse.history;

    return apiData.map((item: any) => {
      // Check if this item has a status (status field exists)
      const hasStatus = item.status && item.status !== null;
      const statusName = hasStatus ? STATUS_MAP[item.status] : null;
      
      // Check if this item has fields
      const hasFields = item.fields && item.fields !== null && Array.isArray(item.fields) && item.fields.length > 0;
      const fieldsText = hasFields ? item.fields.join('\n') : null; // Use newline instead of comma

      // Create properties structure for trip-history-sidebar
      // Show Action + Fields/Status combination
      const header: any = {
        'Action': item.action || 'Unknown Action',
        'Updated By': item.user?.username || 'Unknown User'
      };

      // Add fields or status if available
      if (hasFields) {
        header['Fields Changed'] = fieldsText;
      }
      if (hasStatus) {
        header['Status'] = statusName || 'Unknown';
      }

      const properties = {
        header: header,
        detail: {
          new: { 
            action: item.action || 'Unknown Action',
            ...(hasFields && { fields: fieldsText }),
            ...(hasStatus && { status: statusName || 'Unknown' })
          },
          old: {}
        }
      };

      return {
        id: item.id,
        log_name: item.action || 'Unknown Action',
        description: item.action || 'Unknown Action',
        subject_type: 'Lead',
        log_type: 1,
        event: 'created',
        subject_id: item.lead_id || this.tripId,
        causer_type: 'User',
        causer_id: item.user?.id || 0,
        user_name: item.user?.username || 'Unknown User',
        properties: [JSON.stringify(properties)],
        batch_uuid: null as any,
        created_at: item.created_at || new Date().toISOString(),
        updated_at: item.created_at || new Date().toISOString()
      };
    });
  }
}
